import type { CSSProperties } from 'react';

import { STR } from '../i18n';
import { useBranding } from '../lib/branding';
import { formatDate } from '../lib/format';
import { showToast } from '../lib/toast';

/**
 * The shown-once recovery-code panel, from the 2FA design of record: the code grid, "Copy
 * all", "Download .txt", and the acknowledgement that gates the caller's own primary action.
 * Rendered in exactly two places — the enrolment codes screen and the regeneration dialog —
 * so the treatment can never drift between them.
 *
 * THE CODES LIVE IN THE CALLER'S COMPONENT STATE AND NOWHERE ELSE. This panel receives them
 * as props, renders them, and hands them to the clipboard or a download the person explicitly
 * asks for — it never writes them to storage, a query cache, or a URL. When the caller
 * unmounts, they are gone; the server stores only hashes and can never show them again.
 *
 * The acknowledgement is CONTROLLED by the caller, because what it gates ("Finish set-up",
 * "Done") is the caller's button, not this panel's.
 */
export function RecoveryCodesPanel({
  codes,
  email,
  acknowledged,
  onAcknowledgedChange,
  gridStyle,
}: {
  codes: readonly string[];
  /** Whose codes these are — printed into the downloaded file so a found file names its owner. */
  email: string;
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  /** The enrolment screen widens the demo grid via auto-fill; the dialog keeps two columns. */
  gridStyle?: CSSProperties;
}) {
  const branding = useBranding();
  const brandName = branding.data?.name.trim() ?? '';

  function copyAll() {
    void (async () => {
      try {
        await navigator.clipboard.writeText(codes.join('\n'));
        showToast(STR.twoFactor.codes.copied(codes.length));
      } catch {
        // Clipboard refused (no permission, insecure context): the codes are on screen and
        // selectable, so the person is never stuck — say so instead of failing silently.
        showToast(STR.twoFactor.codes.copyFailed, 'warn');
      }
    })();
  }

  function download() {
    const header =
      brandName === ''
        ? STR.twoFactor.codes.fileHeader('', email).trim().replace(/^—\s*/u, '')
        : STR.twoFactor.codes.fileHeader(brandName, email);
    const lines = [
      header,
      STR.twoFactor.codes.fileGenerated(formatDate(new Date().toISOString())),
      '',
      ...codes,
    ];
    try {
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download =
        brandName === ''
          ? STR.twoFactor.codes.fileName
          : `${brandName.toLowerCase().replace(/\s+/gu, '-')}-${STR.twoFactor.codes.fileName}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 4000);
      showToast(STR.twoFactor.codes.downloaded);
    } catch {
      showToast(STR.twoFactor.codes.downloadFailed, 'warn');
    }
  }

  return (
    <>
      <div className="codes-grid" style={gridStyle}>
        {codes.map((code) => (
          <span className="code-item" key={code}>
            {code}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="ghost btn" style={{ flex: 1 }} onClick={copyAll}>
          {STR.twoFactor.codes.copyAll}
        </button>
        <button type="button" className="ghost btn" style={{ flex: 1 }} onClick={download}>
          {STR.twoFactor.codes.downloadTxt}
        </button>
      </div>
      <label className="ack">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => {
            onAcknowledgedChange(event.target.checked);
          }}
        />
        <span>
          <span className="t1">{STR.twoFactor.codes.ackTitle}</span>
          <span className="t2">{STR.twoFactor.codes.ackNote}</span>
        </span>
      </label>
    </>
  );
}
